import { get, set } from 'idb-keyval';
import {
  clampSetLength,
  DEFAULT_SETTINGS,
  EMPTY_PROGRESS,
  type AppState,
  type Progress,
  type Session,
  type Settings,
} from './state';
import { TEMPLATE_BY_ID } from '../content/templates';
import { TOPIC_BY_ID } from '../content/topics';
import { isValidIsoDate, today } from '../engine/dates';
import { BADGE_BY_ID, MAX_FREEZES } from '../engine/progress';
import { MAX_BOX, MIN_BOX } from '../engine/scheduler';

const KEY = 'shopmath.v1';

/** Only these three slices survive a reload. Screen and tab are ephemeral. */
interface Saved {
  readonly version: 1;
  readonly progress: Progress;
  readonly settings: Settings;
  readonly session: Session | null;
}

/**
 * Everything read back off disk is treated as untrusted.
 *
 * The store is the user's own device, but a half-written record, a build that
 * renamed a template, or a hand-edited export would otherwise crash the app on
 * open — and an app that will not open is the one thing a streak cannot
 * survive. Anything that fails validation falls back to a sane default rather
 * than throwing.
 */
export async function load(): Promise<Partial<AppState>> {
  let raw: unknown;
  try {
    raw = await get(KEY);
  } catch {
    return {};
  }
  if (!isRecord(raw)) return {};

  return {
    progress: readProgress(raw.progress),
    settings: readSettings(raw.settings),
    session: readSession(raw.session),
    // Land back in the drill if she left mid-question.
    screen: readSession(raw.session) ? 'drill' : 'home',
  };
}

export async function save(state: AppState): Promise<void> {
  const payload: Saved = {
    version: 1,
    progress: state.progress,
    settings: state.settings,
    session: state.session,
  };
  try {
    await set(KEY, payload);
  } catch {
    // Storage full or blocked (private mode, quota). Losing a save is bad but
    // crashing the drill she is in the middle of is worse.
  }
}

// ── validation ───────────────────────────────────────────────────────────────

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

const num = (v: unknown, fallback: number, min = 0): number =>
  typeof v === 'number' && Number.isFinite(v) && v >= min ? v : fallback;

const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

function readProgress(v: unknown): Progress {
  if (!isRecord(v)) return EMPTY_PROGRESS;

  const days: Record<string, 'on' | 'freeze' | 'off'> = {};
  if (isRecord(v.days)) {
    for (const [date, mark] of Object.entries(v.days)) {
      if (!isValidIsoDate(date)) continue;
      if (mark === 'on' || mark === 'freeze' || mark === 'off') days[date] = mark;
    }
  }

  const boxes: Record<string, number> = {};
  if (isRecord(v.boxes)) {
    for (const [id, box] of Object.entries(v.boxes)) {
      if (!TEMPLATE_BY_ID.has(id)) continue;
      if (typeof box !== 'number' || !Number.isFinite(box)) continue;
      boxes[id] = Math.max(MIN_BOX, Math.min(MAX_BOX, Math.round(box)));
    }
  }

  const best: Partial<Record<string, number>> = {};
  if (isRecord(v.best)) {
    for (const [key, value] of Object.entries(v.best)) {
      // Drop progress for topics that no longer exist rather than carrying
      // a phantom entry that inflates her completion.
      if (TOPIC_BY_ID.has(key as never) && typeof value === 'number' && value >= 0) {
        best[key] = value;
      }
    }
  }

  return {
    xp: num(v.xp, 0),
    streak: num(v.streak, 0),
    longest: num(v.longest, 0),
    freezes: Math.min(num(v.freezes, EMPTY_PROGRESS.freezes), MAX_FREEZES),
    days,
    lastPracticeDay: isValidIsoDate(v.lastPracticeDay) ? v.lastPracticeDay : null,
    solved: num(v.solved, 0),
    sets: num(v.sets, 0),
    bench: num(v.bench, 0),
    cleanRuns: strings(v.cleanRuns),
    best: best as Progress['best'],
    // A badge id from a build that no longer defines it would render as a
    // blank row in Wins, so unknown ids are dropped rather than displayed.
    earnedBadges: strings(v.earnedBadges).filter((id) => BADGE_BY_ID.has(id)),
    boxes,
    // Templates removed by a content update must not linger in the queue.
    parkedTemplates: strings(v.parkedTemplates).filter((id) => TEMPLATE_BY_ID.has(id)),
  };
}

function readSettings(v: unknown): Settings {
  if (!isRecord(v)) return DEFAULT_SETTINGS;
  return {
    setLength: clampSetLength(num(v.setLength, DEFAULT_SETTINGS.setLength, 1)),
    reduceMotion: bool(v.reduceMotion, DEFAULT_SETTINGS.reduceMotion),
    haptics: bool(v.haptics, DEFAULT_SETTINGS.haptics),
    font: v.font === 'hyperlegible' ? 'hyperlegible' : 'default',
  };
}

function readSession(v: unknown): Session | null {
  if (!isRecord(v)) return null;
  if (!TOPIC_BY_ID.has(v.topicId as never)) return null;

  const draws = Array.isArray(v.draws)
    ? v.draws.filter(
        (d): d is { templateId: string; seed: string } =>
          isRecord(d) &&
          typeof d.templateId === 'string' &&
          typeof d.seed === 'string' &&
          // A build that removed or renamed a template must not resurrect it.
          TEMPLATE_BY_ID.has(d.templateId),
      )
    : [];
  if (draws.length === 0) return null;

  const index = Math.min(num(v.index, 0), draws.length - 1);

  return {
    topicId: v.topicId as Session['topicId'],
    // A session saved without a start date predates streak tracking; crediting
    // it to today is the kind reading, and it cannot inflate anything because
    // recordPractice only ever counts a given day once.
    startedOn: isValidIsoDate(v.startedOn) ? v.startedOn : today(),
    draws,
    index,
    response:
      typeof v.response === 'number' ||
      v.response === 'opp' ||
      v.response === 'adj' ||
      v.response === 'hyp'
        ? v.response
        : null,
    revealed: bool(v.revealed, false),
    right: Math.min(num(v.right, 0), draws.length),
    parked: Array.isArray(v.parked)
      ? v.parked.filter((n): n is number => typeof n === 'number' && n >= 0 && n < draws.length)
      : [],
    stepsShown: num(v.stepsShown, 0),
  };
}

/** Human-portable backup. Used by the export/import panel in Settings. */
export function exportCode(state: AppState): string {
  return btoa(
    unescape(
      encodeURIComponent(
        JSON.stringify({ version: 1, progress: state.progress, settings: state.settings }),
      ),
    ),
  );
}

export function importCode(code: string): Partial<AppState> | null {
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(escape(atob(code.trim()))));
    if (!isRecord(parsed)) return null;
    return {
      progress: readProgress(parsed.progress),
      settings: readSettings(parsed.settings),
    };
  } catch {
    return null;
  }
}
